import React, { useEffect, useState } from "react";
import { Lock, RefreshCw, Mail, Phone, Building2, Calendar, LogOut, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "rciq_admin_key";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const downloadCsv = (rows) => {
  const headers = ["created_at", "name", "email", "organization", "role", "phone", "organization_size", "message"];
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `rciq-demo-requests-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const KeyPrompt = ({ onSubmit, error }) => {
  const [key, setKey] = useState("");
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050913] text-white px-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (key.trim()) onSubmit(key.trim());
        }}
        className="w-full max-w-md rciq-glass-strong rounded-2xl p-8"
        data-testid="admin-key-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300 font-semibold">
              Admin Access
            </div>
            <h1 className="font-display text-xl font-semibold">Demo Request Inbox</h1>
          </div>
        </div>
        <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-key">
          Admin Key
        </label>
        <input
          id="admin-key"
          data-testid="admin-key-input"
          type="password"
          autoFocus
          autoComplete="off"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Enter your admin key"
          className="w-full h-11 px-4 rounded-lg bg-slate-900/60 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20"
        />
        {error && (
          <p className="mt-3 text-xs text-rose-300" data-testid="admin-key-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-key-submit"
          className="mt-5 w-full h-11 rounded-lg rciq-btn-primary text-sm font-semibold inline-flex items-center justify-center gap-2"
        >
          Unlock Inbox
        </button>
        <p className="mt-4 text-[11px] text-slate-500 text-center">
          Authorized personnel only. Submissions contain no PHI.
        </p>
      </form>
    </div>
  );
};

const Row = ({ r }) => (
  <tr className="border-b border-white/5 hover:bg-white/[0.02] transition" data-testid="admin-demo-row">
    <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">
      <div className="flex items-center gap-1.5">
        <Calendar className="w-3 h-3" />
        {formatDate(r.created_at)}
      </div>
    </td>
    <td className="py-3 px-4">
      <div className="font-medium text-white text-sm">{r.name}</div>
      {r.role && <div className="text-[11px] text-slate-500">{r.role}</div>}
    </td>
    <td className="py-3 px-4 text-sm">
      <a href={`mailto:${r.email}`} className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
        <Mail className="w-3 h-3" />
        {r.email}
      </a>
      {r.phone && (
        <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
          <Phone className="w-3 h-3" /> {r.phone}
        </div>
      )}
    </td>
    <td className="py-3 px-4 text-sm text-slate-200">
      <div className="inline-flex items-center gap-1.5">
        <Building2 className="w-3 h-3 text-slate-500" />
        {r.organization}
      </div>
      {r.organization_size && (
        <div className="text-[11px] text-slate-500 mt-1">{r.organization_size}</div>
      )}
    </td>
    <td className="py-3 px-4 text-xs text-slate-300 max-w-md">
      {r.message ? (
        <div className="whitespace-pre-wrap leading-relaxed">{r.message}</div>
      ) : (
        <span className="text-slate-600 italic">— no message —</span>
      )}
    </td>
  </tr>
);

const AdminDemos = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authError, setAuthError] = useState("");

  const fetchData = async (k) => {
    setLoading(true);
    setError("");
    setAuthError("");
    try {
      const res = await fetch(`${API}/demo-requests`, {
        headers: { "X-Admin-Key": k },
      });
      if (res.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
        setAuthError("Invalid admin key.");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data);
    } catch (e) {
      setError(e.message || "Failed to load demo requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) fetchData(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleUnlock = (k) => {
    sessionStorage.setItem(STORAGE_KEY, k);
    setKey(k);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setRows([]);
  };

  if (!key) return <KeyPrompt onSubmit={handleUnlock} error={authError} />;

  return (
    <div className="min-h-screen bg-[#050913] text-white">
      <header className="border-b border-white/5 bg-[#050913]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-cyan-300 font-semibold">
              Admin · Demo Requests
            </div>
            <h1 className="font-display text-2xl font-semibold mt-0.5">
              {rows.length} {rows.length === 1 ? "submission" : "submissions"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchData(key)}
              disabled={loading}
              className="h-10 px-4 rounded-lg rciq-btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-export-btn"
              onClick={() => downloadCsv(rows)}
              disabled={!rows.length}
              className="h-10 px-4 rounded-lg rciq-btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              data-testid="admin-signout-btn"
              onClick={handleSignOut}
              className="h-10 px-4 rounded-lg rciq-btn-ghost text-sm inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 rounded-lg border border-rose-400/30 bg-rose-400/10 text-rose-200 px-4 py-3 text-sm" data-testid="admin-error">
            {error}
          </div>
        )}

        {loading && !rows.length ? (
          <div className="text-center py-20 text-slate-400 text-sm" data-testid="admin-loading">
            Loading demo requests…
          </div>
        ) : !rows.length ? (
          <div className="text-center py-20 text-slate-500 text-sm" data-testid="admin-empty">
            No demo requests yet. New submissions will appear here.
          </div>
        ) : (
          <div className="rciq-glass rounded-xl overflow-hidden border border-white/5">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-demo-table">
                <thead className="bg-white/[0.03]">
                  <tr className="border-b border-white/5">
                    <th className="py-3 px-4 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">When</th>
                    <th className="py-3 px-4 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Contact</th>
                    <th className="py-3 px-4 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Email · Phone</th>
                    <th className="py-3 px-4 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Organization</th>
                    <th className="py-3 px-4 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Row key={r.id} r={r} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-600 text-center">
          Submissions stored in MongoDB · Filter, sort, and email integrations are roadmap items.
        </p>
      </main>
    </div>
  );
};

export default AdminDemos;
